import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import ConfirmModal from "../../../../components/ConfirmModal";
import Input from "../../../../components/Form/Input/input";
import { Label as LabelText } from "../../../../components/Form/Label/styles";
import { Form, FormGroup } from "../../../../components/Form/styles";
import { Box } from "../../../../components/Layout";
import { OptionTiles } from "../../../../components/OptionTiles";
import SaveBar from "../../../../components/SaveBar";
import { SubHeader } from "../../../../components/SubHeader";
import { useCurrentPage, withPage } from "../../../../hooks/CurrentPage";
import {
  TESTIMONIALS_MAX_ITEMS,
  TESTIMONIALS_MAX_TITLE,
  TestimonialItem,
  TestimonialsSection,
} from "../../../../model/site";
import { clone, deepEqual } from "../../../../utils/objects";
import ItemsTable, { Item } from "../../components/ItemsTable";
import { useSection } from "../../hooks/Section";
import AlignCenter from "../Highlights/pages/Item/components/icons/AlignCenter";
import AlignLeft from "../Highlights/pages/Item/components/icons/AlignLeft";
import { navigationState } from "../Sections/navigationState";
import AlignRight from "./icons/AlignRight";
import { AlignmentButton, AlignmentRow, Container, LimitNote } from "./styles";
import { TestimonialsNavigationState } from "./testimonialsNavigationState";

const prefix = "pages.editor.testimonials";

const ALIGNMENTS = [
  { value: "LEFT", label: "alignLeft", Icon: AlignLeft },
  { value: "CENTER", label: "alignCenter", Icon: AlignCenter },
  { value: "RIGHT", label: "alignRight", Icon: AlignRight },
] as const;

// One at a time sliding, or all of them one under the other; each option draws its layout
const LAYOUTS = [
  {
    value: "SLIDE",
    drawing: (
      <svg width="64" height="40" viewBox="0 0 64 40" fill="none" aria-hidden="true">
        <rect x="14" y="2" width="36" height="26" rx="4" fill="currentColor" opacity="0.35" />
        <rect x="2" y="6" width="8" height="18" rx="3" fill="currentColor" opacity="0.15" />
        <rect x="54" y="6" width="8" height="18" rx="3" fill="currentColor" opacity="0.15" />
        <circle cx="26" cy="36" r="2" fill="currentColor" />
        <circle cx="32" cy="36" r="2" fill="currentColor" opacity="0.35" />
        <circle cx="38" cy="36" r="2" fill="currentColor" opacity="0.35" />
      </svg>
    ),
  },
  {
    value: "LIST",
    drawing: (
      <svg width="64" height="40" viewBox="0 0 64 40" fill="none" aria-hidden="true">
        <rect x="8" y="2" width="48" height="8" rx="3" fill="currentColor" opacity="0.35" />
        <rect x="8" y="15" width="48" height="1" fill="currentColor" opacity="0.5" />
        <rect x="8" y="21" width="48" height="8" rx="3" fill="currentColor" opacity="0.35" />
        <rect x="8" y="34" width="48" height="1" fill="currentColor" opacity="0.5" />
      </svg>
    ),
  },
] as const;

// A testimonial as the list of items shows it: who said it as the title, what was said under it
const toRow = ({ id, name, text, image, active }: TestimonialItem): Item => ({
  id,
  title: name,
  description: text,
  thumbnailType: "IMAGE",
  image,
  active,
});

const TestimonialsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pageId } = useCurrentPage();

  const testimonialsNavigationState = useRef(navigationState.get() as TestimonialsNavigationState | undefined);
  const restoreState = useCallback(
    (newly: unknown, section: TestimonialsSection) => {
      const coming = testimonialsNavigationState.current?.currentSection;
      // back from the screen of a testimonial: the section as it was left, with what that screen changed
      if (coming?.type === "testimonials") {
        section.pageTitle = coming.pageTitle;
        section.items = coming.items;
        section.alignment = coming.alignment;
        section.layout = coming.layout;
      } else if (newly) {
        // a new section comes titled, as the other sections do (the title can be changed or cleared)
        section.pageTitle = t(`${prefix}.header.title`);
        section.items = [];
        section.alignment = "CENTER";
      }
      return section;
    },
    [t, testimonialsNavigationState]
  );

  const { section, setSection, saveSection, hasUnsavedChanges } = useSection<TestimonialsSection>(
    "testimonials",
    restoreState
  );

  const [idRemoving, setIdRemoving] = useState<string>();
  const isFull = section.items.length >= TESTIMONIALS_MAX_ITEMS;

  const goEditItemPage = (id?: string) => {
    const previous = testimonialsNavigationState.current;
    const state: TestimonialsNavigationState = {
      currentSection: clone<TestimonialsSection>(section),
      defaultSection:
        previous?.defaultSection?.type === "testimonials"
          ? previous.defaultSection
          : clone<TestimonialsSection>(section),
      editingTestimonial: section.items.find(testimonial => testimonial.id === id),
    };
    navigationState.set(state);
    navigate(withPage("/editor/testimonials/item", pageId));
  };

  const removeTestimonial = () => {
    setSection({ ...section, items: section.items.filter(({ id }) => id !== idRemoving) });
    setIdRemoving(undefined);
  };

  // the list gives back its rows (reordered, shown or hidden): each testimonial keeps its own fields
  const onRowsUpdate = (rows: Item[]) =>
    setSection({
      ...section,
      items: rows.flatMap(({ id, active }) => {
        const testimonial = section.items.find(existing => existing.id === id);
        return testimonial ? [active === undefined ? testimonial : { ...testimonial, active }] : [];
      }),
    });

  const hasUnsavedChangesWrapper = (): boolean => {
    const defaultSection = testimonialsNavigationState.current?.defaultSection;
    return defaultSection?.type === "testimonials" ? !deepEqual(section, defaultSection) : hasUnsavedChanges();
  };

  return (
    <Container>
      <SubHeader
        title={t(`${prefix}.header.title`)}
        crumbs={[{ label: t("pages.editor.preview.footer.actions.sections"), to: "/editor/sections" }]}
        hasUnsavedChanges={hasUnsavedChangesWrapper}
      />
      {idRemoving && (
        <ConfirmModal
          type="DANGER"
          title={t(`${prefix}.actions.remove.confirmModal.title`)}
          cancelTitle={t(`${prefix}.actions.remove.confirmModal.cancelButton`)}
          confirmButtonTitle={t(`${prefix}.actions.remove.confirmModal.confirmButton`)}
          onCancel={() => setIdRemoving(undefined)}
          onConfirm={removeTestimonial}
        />
      )}
      <Form aria-label={t(`${prefix}.header.title`)} onSubmit={event => event.preventDefault()}>
        <Box borderBottom>
          <FormGroup>
            <Input
              id="pageTitle"
              type="text"
              maxLength={TESTIMONIALS_MAX_TITLE}
              value={section.pageTitle || ""}
              onChange={e => setSection({ ...section, pageTitle: e.target.value })}
              label={t(`${prefix}.form.pageTitle`)}
            />
          </FormGroup>
        </Box>
        <Box borderBottom>
          <LabelText as="p" id="layout-label">
            {t(`${prefix}.form.layout.label`)}
          </LabelText>
          <OptionTiles $columns={2} role="radiogroup" aria-labelledby="layout-label">
            {LAYOUTS.map(({ value, drawing }) => (
              <button
                type="button"
                role="radio"
                key={value}
                aria-checked={(section.layout || "SLIDE") === value}
                onClick={() => setSection({ ...section, layout: value })}>
                {drawing}
                {t(`${prefix}.form.layout.${value}`)}
              </button>
            ))}
          </OptionTiles>
        </Box>
        <Box borderBottom>
          <FormGroup>
            <AlignmentRow>
              <LabelText as="p">{t(`${prefix}.form.alignment`)}</LabelText>
              <div className="options">
                {ALIGNMENTS.map(({ value, label, Icon }) => (
                  <AlignmentButton
                    type="button"
                    key={value}
                    aria-label={t(`${prefix}.form.${label}`)}
                    aria-pressed={section.alignment === value}
                    onClick={() => setSection({ ...section, alignment: value })}>
                    <Icon />
                  </AlignmentButton>
                ))}
              </div>
            </AlignmentRow>
          </FormGroup>
        </Box>
        <Box>
          <ItemsTable
            onSortUpdate={onRowsUpdate}
            onAddClick={() => goEditItemPage()}
            addDisabled={isFull}
            onEditClick={goEditItemPage}
            onRemoveClick={setIdRemoving}
            items={section.items.map(toRow)}
            title={t(`${prefix}.form.table.title`)}
            addButtonTitle={t(`${prefix}.form.table.addButton`)}
          />
          {isFull && <LimitNote>{t(`${prefix}.form.limit`, { count: TESTIMONIALS_MAX_ITEMS })}</LimitNote>}
        </Box>
        <SaveBar onSave={saveSection} hasUnsavedChanges={hasUnsavedChangesWrapper} />
      </Form>
    </Container>
  );
};

export default TestimonialsPage;
